import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Upload, ImageIcon, Minus, Plus, ChevronRight, ChevronLeft, Check, Lightbulb, MapPin,
  X, Wifi, Monitor, Printer, Wind, Coffee, Sofa, Car, Accessibility, ChefHat,
  Projector, PenTool, Mic, Sparkles, Camera, TrendingUp, Tag,
  Calendar as CalendarIcon, Clock, Copy, SlidersHorizontal, Trash2,
  DollarSign, Percent, Wallet, Star, Info,
} from "lucide-react";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/merchant/spaces/new")({
  head: () => ({
    meta: [
      { title: "Add new space — Spayzo Merchant" },
      { name: "description", content: "Create a new space in 4 simple steps." },
    ],
  }),
  component: NewSpacePage,
});

const STEP_KEYS = [1, 2, 3, 4] as const;

const SPACE_TYPES = ["Workspace", "Studio", "Meeting", "Event hall", "Café booth"];
const NOISE = ["Silent", "Quiet", "Moderate", "Lively"];

const MOODS = [
  { key: "focused", emoji: "🎯" },
  { key: "creative", emoji: "✨" },
  { key: "social", emoji: "🗣️" },
  { key: "calm", emoji: "😌" },
  { key: "energetic", emoji: "⚡" },
  { key: "cozy", emoji: "🛋️" },
  { key: "professional", emoji: "💼" },
  { key: "relaxed", emoji: "🌿" },
  { key: "vibrant", emoji: "🎵" },
];

const AMENITY_GROUPS: { group: string; items: { key: string; icon: React.ElementType; custom?: string }[] }[] = [
  {
    group: "Connectivity",
    items: [
      { key: "wifi", icon: Wifi },
      { key: "screen", icon: Monitor },
      { key: "printer", icon: Printer },
    ],
  },
  {
    group: "Comfort",
    items: [
      { key: "ac", icon: Wind },
      { key: "coffee", icon: Coffee },
      { key: "lounge", icon: Sofa },
    ],
  },
  {
    group: "Facilities",
    items: [
      { key: "parking", icon: Car },
      { key: "accessible", icon: Accessibility },
      { key: "kitchen", icon: ChefHat },
    ],
  },
  {
    group: "Equipment",
    items: [
      { key: "projector", icon: Projector },
      { key: "whiteboard", icon: PenTool },
      { key: "microphone", icon: Mic },
    ],
  },
];

const HOUSE_RULES = ["pets", "food", "smoking", "music", "events"];

const SAMPLE_PHOTOS = [
  "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
  "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=800&q=80",
  "https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=800&q=80",
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DUR_OPTS = ["30min", "1hour", "2hours", "4hours", "8hours", "12hours", "1day"];
const BUFFER_OPTS = ["0min", "15min", "30min", "1hour"];
const WINDOW_OPTS = ["7days", "14days", "30days", "60days", "90days"];
const CUTOFF_OPTS = ["0hours", "1hour", "2hours", "12hours", "24hours"];
const CANCEL_POLICIES = ["Flexible", "Moderate", "Strict"];

function NewSpacePage() {
  return <SpaceWizard mode="create" />;
}

export function SpaceWizard({ mode = "create" }: { mode?: "create" | "edit" }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const BackChevron = isAr ? ChevronRight : ChevronLeft;
  const ForwardChevron = isAr ? ChevronLeft : ChevronRight;
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const isEdit = mode === "edit";

  const ns = "pages.merchant.spacesNew";
  const STEPS = STEP_KEYS.map((n) => ({ n, label: t(`${ns}.steps.${n}`) }));

  // Step 1
  const [cover, setCover] = useState<string | null>(null);
  const [type, setType] = useState("Workspace");
  const [noise, setNoise] = useState("Silent");
  const [capacity, setCapacity] = useState(8);
  const [minHrs, setMinHrs] = useState(1);
  const [desc, setDesc] = useState("");

  // Step 2
  const [photos, setPhotos] = useState<string[]>(SAMPLE_PHOTOS);
  const [moods, setMoods] = useState<string[]>(["focused", "creative", "calm", "relaxed"]);
  const [amenities, setAmenities] = useState<string[]>([
    "wifi", "screen", "ac", "coffee", "lounge", "parking", "accessible", "whiteboard",
  ]);
  const [amenityGroups, setAmenityGroups] = useState(AMENITY_GROUPS);
  const [newAmenityLabel, setNewAmenityLabel] = useState("");
  const [newAmenityGroup, setNewAmenityGroup] = useState<string>(AMENITY_GROUPS[0].group);
  const [newAmenityCustomGroup, setNewAmenityCustomGroup] = useState("");
  const amenityLabel = (item: { key: string; custom?: string }) =>
    item.custom ?? t(`${ns}.amenityLabels.${item.key}`, { defaultValue: item.key });
  const groupLabel = (group: string) => t(`${ns}.amenityGroups.${group}`, { defaultValue: group });
  const addAmenity = () => {
    const label = newAmenityLabel.trim();
    const targetGroup = newAmenityGroup === "__new__" ? newAmenityCustomGroup.trim() : newAmenityGroup;
    if (!label || !targetGroup) return;
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "-") + "-" + Math.random().toString(36).slice(2, 6);
    setAmenityGroups((prev) => {
      const exists = prev.find((g) => g.group === targetGroup);
      const newItem = { key, icon: Check, custom: label };
      if (exists) {
        return prev.map((g) => (g.group === targetGroup ? { ...g, items: [...g.items, newItem] } : g));
      }
      return [...prev, { group: targetGroup, items: [newItem] }];
    });
    setAmenities((prev) => [...prev, key]);
    setNewAmenityLabel("");
    setNewAmenityCustomGroup("");
    if (newAmenityGroup === "__new__") setNewAmenityGroup(targetGroup);
  };
  const [rules, setRules] = useState<Record<string, boolean>>({ food: true, events: true });
  const [extraRules, setExtraRules] = useState("");
  const [tags, setTags] = useState<string[]>(["co-working", "quiet", "creative studio", "freelancer-friendly"]);
  const [tagInput, setTagInput] = useState("");

  // Step 3
  const [schedule, setSchedule] = useState<Record<string, { open: boolean; from: string; to: string }>>({
    Monday: { open: true, from: "08:00", to: "18:00" },
    Tuesday: { open: true, from: "08:00", to: "18:00" },
    Wednesday: { open: true, from: "08:00", to: "18:00" },
    Thursday: { open: true, from: "09:00", to: "17:00" },
    Friday: { open: true, from: "08:00", to: "16:00" },
    Saturday: { open: false, from: "10:00", to: "16:00" },
    Sunday: { open: false, from: "10:00", to: "16:00" },
  });
  const [minDur, setMinDur] = useState("1hour");
  const [maxDur, setMaxDur] = useState("8hours");
  const [buffer, setBuffer] = useState("30min");
  const [maxGuests, setMaxGuests] = useState(12);
  const [advWindow, setAdvWindow] = useState("30days");
  const [cutoff, setCutoff] = useState("2hours");
  const [overrides, setOverrides] = useState<{ date: string; kind: "Closed" | "Custom hours"; note: string }[]>([
    { date: "Jan 1, 2025", kind: "Closed", note: "New Year's Day — full day closure" },
    { date: "Jan 16, 2025", kind: "Custom hours", note: "10:00 AM – 2:00 PM only (partial day)" },
  ]);

  // Step 4 — Pricing
  const [hourly, setHourly] = useState(85);
  const [daily, setDaily] = useState(600);
  const [weeklyPkg, setWeeklyPkg] = useState(2800);
  const [monthlyPkg, setMonthlyPkg] = useState(9500);
  const [currency, setCurrency] = useState("SAR");
  const [weekendPricing, setWeekendPricing] = useState(true);
  const [weekendUplift, setWeekendUplift] = useState(15);
  const [earlyBird, setEarlyBird] = useState(true);
  const [earlyBirdPct, setEarlyBirdPct] = useState(10);
  const [weeklyDisc, setWeeklyDisc] = useState(true);
  const [weeklyDiscPct, setWeeklyDiscPct] = useState(15);
  const [depositRequired, setDepositRequired] = useState(false);
  const [depositPct, setDepositPct] = useState(20);
  const [cancelPolicy, setCancelPolicy] = useState("Flexible");
  const [estHours, setEstHours] = useState(80);

  const platformFeePct = 10;
  const gatewayPct = 2.5;
  const gross = estHours * hourly;
  const platformFee = Math.round((gross * platformFeePct) / 100);
  const gatewayFee = Math.round((gross * gatewayPct) / 100);
  const payout = gross - platformFee - gatewayFee;

  const onPickCover = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setCover(URL.createObjectURL(f));
  };

  const onPickPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const urls = files.slice(0, 15 - photos.length).map((f) => URL.createObjectURL(f));
    setPhotos((p) => [...p, ...urls]);
  };

  const toggle = <T,>(list: T[], v: T, set: (l: T[]) => void) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const addTag = () => {
    const tg = tagInput.trim().toLowerCase();
    if (tg && !tags.includes(tg) && tags.length < 12) setTags((p) => [...p, tg]);
    setTagInput("");
  };

  return (
    <MerchantShell
      title={isEdit ? t(`${ns}.editTitle`) : t(`${ns}.title`)}
      subtitle={t(`${ns}.subtitles.${step}`)}
      actions={
        <Button asChild variant="outline" className="h-10 rounded-xl">
          <Link to="/merchant/spaces">{t(`${ns}.cancel`)}</Link>
        </Button>
      }
    >
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/merchant/spaces" className="hover:text-foreground">{t(`${ns}.breadcrumbSpaces`)}</Link>
        <ForwardChevron className="h-3 w-3" />
        <span className="hover:text-foreground">{isEdit ? t(`${ns}.editTitle`) : t(`${ns}.title`)}</span>
        <ForwardChevron className="h-3 w-3" />
        <span className="font-semibold text-foreground">{STEPS[step - 1].label}</span>
      </div>

      {/* Stepper */}
      <Card className="rounded-2xl border-border/60 p-4 shadow-soft sm:p-5">
        <ol className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {STEPS.map((s) => {
            const done = s.n < step;
            const active = s.n === step;
            return (
              <li key={s.n} className="flex items-center gap-3">
                <div
                  className={cn(
                    "grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold ring-1 transition",
                    done && "bg-[#5958F3] text-white ring-[#5958F3]",
                    active && "bg-[#5958F3]/10 text-[#5958F3] ring-[#5958F3]",
                    !done && !active && "bg-muted text-muted-foreground ring-border",
                  )}
                >
                  {done ? <Check className="h-4 w-4" /> : s.n}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(`${ns}.stepLabel`, { n: s.n })}
                  </p>
                  <p className={cn("truncate text-sm font-semibold", active ? "text-foreground" : "text-muted-foreground")}>
                    {s.label}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </Card>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
        {/* Sidebar tips */}
        <aside className="order-2 space-y-4 lg:order-1">
          {step === 1 && (
            <Card className="sticky top-6 overflow-hidden rounded-2xl border-[#5958F3]/20 bg-gradient-to-br from-[#5958F3]/8 to-transparent p-5 shadow-soft">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3] text-white">
                  <Lightbulb className="h-4 w-4" />
                </div>
                <h3 className="font-display text-sm font-bold">{t(`${ns}.tips.listing.title`)}</h3>
              </div>
              <ol className="mt-4 space-y-3 text-xs text-muted-foreground">
                {(t(`${ns}.tips.listing.items`, { returnObjects: true }) as string[]).map((tip, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#5958F3]/15 text-[10px] font-bold text-[#5958F3]">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {step === 2 && (
            <>
              <Card className="overflow-hidden rounded-2xl border-[#5958F3]/20 bg-gradient-to-br from-[#5958F3]/8 to-transparent p-5 shadow-soft">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3] text-white">
                    <Camera className="h-4 w-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold">{t(`${ns}.tips.photo.title`)}</h3>
                </div>
                <ul className="mt-4 space-y-2.5 text-xs text-muted-foreground">
                  {(t(`${ns}.tips.photo.items`, { returnObjects: true }) as string[]).map((tp) => (
                    <li key={tp} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#5958F3]" />
                      <span className="leading-relaxed">{tp}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3]/10 text-[#5958F3]">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold">{t(`${ns}.tips.mood.title`)}</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                  {t(`${ns}.tips.mood.desc`)}
                </p>
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-500/10 p-2.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  <TrendingUp className="h-3.5 w-3.5" />
                  {t(`${ns}.tips.mood.banner`)}
                </div>
              </Card>

              <Card className="rounded-2xl border-border/60 bg-[#5958F3]/5 p-5 shadow-soft">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3]/10 text-[#5958F3]">
                    <Lightbulb className="h-4 w-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold">{t(`${ns}.tips.amenity.title`)}</h3>
                </div>
                <ol className="mt-4 space-y-3 text-xs text-muted-foreground">
                  {(t(`${ns}.tips.amenity.items`, { returnObjects: true }) as string[]).map((tip, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#5958F3]/15 text-[10px] font-bold text-[#5958F3]">
                        {i + 1}
                      </span>
                      <span className="leading-relaxed">{tip}</span>
                    </li>
                  ))}
                </ol>
              </Card>
            </>
          )}

          {step === 3 && (
            <>
              <Card className="overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft">
                <div className="mb-3 flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3]/10 text-[#5958F3]">
                    <CalendarIcon className="h-4 w-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold">{t(`${ns}.tips.availability.title`)}</h3>
                </div>
                <MiniCalendar schedule={schedule} dayLabel={(d) => t(`${ns}.days.${d}`)} isAr={isAr} />
                <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#5958F3]" /> {t(`${ns}.legend.available`)}</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> {t(`${ns}.legend.partial`)}</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" /> {t(`${ns}.legend.closed`)}</span>
                </div>
              </Card>

              <Card className="overflow-hidden rounded-2xl border-[#5958F3]/20 bg-gradient-to-br from-[#5958F3]/8 to-transparent p-5 shadow-soft">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3] text-white">
                    <Lightbulb className="h-4 w-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold">{t(`${ns}.tips.scheduling.title`)}</h3>
                </div>
                <ol className="mt-4 space-y-3 text-xs text-muted-foreground">
                  {(t(`${ns}.tips.scheduling.items`, { returnObjects: true }) as string[]).map((tip, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#5958F3]/15 text-[10px] font-bold text-[#5958F3]">
                        {i + 1}
                      </span>
                      <span className="leading-relaxed">{tip}</span>
                    </li>
                  ))}
                </ol>
              </Card>

              <div className="grid grid-cols-2 gap-3">
                <Card className="rounded-2xl border-border/60 p-4 shadow-soft">
                  <p className="font-display text-2xl font-bold">
                    {Object.values(schedule).filter((d) => d.open).length}
                  </p>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t(`${ns}.stats.daysOpen`)}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-600">
                    <Check className="h-3 w-3" /> {t(`${ns}.stats.monFri`)}
                  </p>
                </Card>
                <Card className="rounded-2xl border-border/60 p-4 shadow-soft">
                  <p className="font-display text-2xl font-bold">10h</p>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t(`${ns}.stats.dailyWindow`)}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" /> {t(`${ns}.stats.amPm`)}
                  </p>
                </Card>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              {/* Live preview */}
              <Card className="overflow-hidden rounded-2xl border-border/60 p-0 shadow-soft">
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                  <img
                    src={cover ?? photos[0] ?? SAMPLE_PHOTOS[0]}
                    alt="Space preview"
                    className="h-full w-full object-cover"
                  />
                  {(weekendPricing || earlyBird || weeklyDisc) && (
                    <span className="absolute start-3 top-3 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold text-white shadow-soft">
                      {Math.max(weekendPricing ? weekendUplift : 0, earlyBird ? earlyBirdPct : 0, weeklyDisc ? weeklyDiscPct : 0)}% off
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t(`${ns}.livePreview.label`)}</p>
                  <h3 className="mt-1 font-display text-base font-bold">{t(`${ns}.livePreview.name`)}</h3>
                  <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" /> {t(`${ns}.livePreview.location`)}
                  </p>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span className="font-display text-xl font-bold text-[#5958F3]">{currency} {hourly}</span>
                    <span className="text-xs text-muted-foreground">{t(`${ns}.livePreview.perHour`)}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                    <span className="rounded-full bg-muted px-2 py-0.5 font-medium">{t(`${ns}.amenityLabels.wifi`)}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 font-medium">{t(`${ns}.amenityLabels.ac`)}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 font-medium">{t(`${ns}.livePreview.guests`, { count: capacity })}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border/60 pt-3 text-center">
                    <div>
                      <p className="inline-flex items-center gap-1 font-display text-sm font-bold"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> 4.8</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(`${ns}.livePreview.rating`)}</p>
                    </div>
                    <div>
                      <p className="font-display text-sm font-bold">{minHrs}h</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(`${ns}.livePreview.min`)}</p>
                    </div>
                    <div>
                      <p className="font-display text-sm font-bold">{capacity}</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(`${ns}.livePreview.maxGuests`)}</p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Estimated earnings */}
              <Card className="overflow-hidden rounded-2xl border-[#5958F3]/20 bg-gradient-to-br from-[#5958F3]/8 to-transparent p-5 shadow-soft">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t(`${ns}.earnings.title`)}</p>
                <p className="mt-1 font-display text-3xl font-bold text-[#5958F3]">{currency} {payout.toLocaleString()}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {t(`${ns}.earnings.basedOn`, { hours: estHours, currency, price: hourly })}
                </p>
                <div className="mt-4 space-y-2 border-t border-border/60 pt-3 text-xs">
                  <Row label={t(`${ns}.earnings.gross`)} value={`${currency} ${gross.toLocaleString()}`} />
                  <Row label={t(`${ns}.earnings.platformFee`, { pct: platformFeePct })} value={`- ${currency} ${platformFee.toLocaleString()}`} muted />
                  <Row label={t(`${ns}.earnings.gatewayFee`, { pct: gatewayPct })} value={`- ${currency} ${gatewayFee.toLocaleString()}`} muted />
                  <div className="flex items-center justify-between border-t border-border/60 pt-2 text-sm font-bold">
                    <span>{t(`${ns}.earnings.payout`)}</span>
                    <span className="text-[#5958F3]">{currency} {payout.toLocaleString()}</span>
                  </div>
                </div>
              </Card>

              {/* Tips */}
              <Card className="overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3] text-white">
                    <Lightbulb className="h-4 w-4" />
                  </div>
                  <h3 className="font-display text-sm font-bold">{t(`${ns}.tips.pricing.title`)}</h3>
                </div>
                <ol className="mt-4 space-y-3 text-xs text-muted-foreground">
                  {(t(`${ns}.tips.pricing.items`, { returnObjects: true }) as string[]).map((tip, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#5958F3]/15 text-[10px] font-bold text-[#5958F3]">
                        {i + 1}
                      </span>
                      <span className="leading-relaxed">{tip}</span>
                    </li>
                  ))}
                </ol>
              </Card>
            </>
          )}
        </aside>


        {/* Main content */}
        <div className="order-1 space-y-5 lg:order-2">
          {step === 1 && (
            <>
              <Section title={t(`${ns}.sections.coverPhoto`)}>
                <label className="group relative flex aspect-[16/9] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-border bg-surface transition hover:border-[#5958F3] hover:bg-[#5958F3]/5">
                  {cover ? (
                    <img src={cover} alt="Cover" className="h-full w-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[#5958F3]/10 text-[#5958F3]">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-3 text-sm font-semibold">{t(`${ns}.fields.coverTitle`)}</p>
                      <p className="text-xs text-muted-foreground">{t(`${ns}.fields.coverHintDrag`)}</p>
                      <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                        <ImageIcon className="h-3 w-3" /> {t(`${ns}.fields.coverHintSpecs`)}
                      </p>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={onPickCover} className="hidden" />
                </label>
              </Section>

              <Section title={t(`${ns}.sections.spaceType`)}>
                <div className="flex flex-wrap gap-2">
                  {SPACE_TYPES.map((ty) => {
                    const on = ty === type;
                    return (
                      <button
                        key={ty}
                        type="button"
                        onClick={() => setType(ty)}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-medium transition",
                          on
                            ? "border-[#5958F3] bg-[#5958F3] text-white shadow-soft"
                            : "border-border bg-background hover:border-[#5958F3]/50",
                        )}
                      >
                        {on && <Check className="h-3.5 w-3.5" />}
                        {t(`${ns}.spaceTypes.${ty}`)}
                      </button>
                    );
                  })}
                </div>
              </Section>

              <Section title={t(`${ns}.sections.spaceDetails`)}>
                <div className="grid gap-4">
                  <Field label={t(`${ns}.fields.spaceName`)}>
                    <Input placeholder={t(`${ns}.placeholders.spaceName`)} className="h-11 rounded-xl" />
                  </Field>
                  <Field label={t(`${ns}.fields.tagline`)}>
                    <Input placeholder={t(`${ns}.placeholders.tagline`)} className="h-11 rounded-xl" />
                  </Field>
                  <Field label={t(`${ns}.fields.description`)}>
                    <Textarea
                      value={desc}
                      onChange={(e) => setDesc(e.target.value.slice(0, 500))}
                      placeholder={t(`${ns}.placeholders.description`)}
                      className="min-h-32 rounded-xl"
                    />
                    <p className="mt-1 text-end text-[11px] text-muted-foreground">{t(`${ns}.fields.descCount`, { count: desc.length })}</p>
                  </Field>
                </div>
              </Section>

              <Section title={t(`${ns}.sections.capacitySpecs`)}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t(`${ns}.fields.maxCapacity`)}>
                    <Stepper value={capacity} onChange={setCapacity} min={1} t={t} />
                  </Field>
                  <Field label={t(`${ns}.fields.minBooking`)}>
                    <Stepper value={minHrs} onChange={setMinHrs} min={1} t={t} />
                  </Field>
                  <Field label={t(`${ns}.fields.floorSize`)}>
                    <Input type="number" placeholder={t(`${ns}.placeholders.floorSize`)} className="h-11 rounded-xl" />
                  </Field>
                  <Field label={t(`${ns}.fields.noiseLevel`)}>
                    <div className="flex flex-wrap gap-2">
                      {NOISE.map((n) => {
                        const on = n === noise;
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setNoise(n)}
                            className={cn(
                              "rounded-xl border px-3 py-2 text-xs font-medium transition",
                              on
                                ? "border-[#5958F3] bg-[#5958F3] text-white"
                                : "border-border bg-background hover:border-[#5958F3]/50",
                            )}
                          >
                            {t(`${ns}.noiseLevels.${n}`)}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </div>
              </Section>

              <Section title={t(`${ns}.sections.location`)}>
                <div className="grid gap-4">
                  <Field label={t(`${ns}.fields.mapsLink`)}>
                    <div className="relative">
                      <MapPin className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input placeholder={t(`${ns}.placeholders.mapsLink`)} className="h-11 rounded-xl ps-10" />
                    </div>
                  </Field>
                  <Field label={t(`${ns}.fields.addressLine1`)}>
                    <Input placeholder={t(`${ns}.placeholders.address1`)} className="h-11 rounded-xl" />
                  </Field>
                  <Field label={t(`${ns}.fields.addressLine2`)}>
                    <Input placeholder={t(`${ns}.placeholders.address2`)} className="h-11 rounded-xl" />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label={t(`${ns}.fields.city`)}>
                      <Input placeholder={t(`${ns}.placeholders.city`)} className="h-11 rounded-xl" />
                    </Field>
                    <Field label={t(`${ns}.fields.district`)}>
                      <Input placeholder={t(`${ns}.placeholders.district`)} className="h-11 rounded-xl" />
                    </Field>
                    <Field label={t(`${ns}.fields.postal`)}>
                      <Input placeholder={t(`${ns}.placeholders.postal`)} className="h-11 rounded-xl" />
                    </Field>
                  </div>
                </div>
              </Section>
            </>
          )}

          {step === 2 && (
            <>
              {/* Space Photos */}
              <Section
                title={t(`${ns}.sections.spacePhotos`)}
                icon={<ImageIcon className="h-4 w-4" />}
                right={<span className="text-xs text-muted-foreground">{t(`${ns}.photos.count`, { count: photos.length })}</span>}
              >
                <p className="mb-3 text-xs text-muted-foreground">
                  {t(`${ns}.photos.uploadDesc`)}
                </p>
                <label className="group flex aspect-[16/6] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border bg-surface transition hover:border-[#5958F3] hover:bg-[#5958F3]/5">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#5958F3]/10 text-[#5958F3]">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm font-semibold">{t(`${ns}.photos.dropTitle`)}</p>
                  <p className="text-[11px] text-muted-foreground">{t(`${ns}.photos.dropHint`)}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#5958F3] px-3.5 py-2 text-xs font-semibold text-white shadow-soft">
                    <Upload className="h-3.5 w-3.5" /> {t(`${ns}.photos.browse`)}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onPickPhotos}
                    className="hidden"
                  />
                </label>

                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {photos.map((src, i) => (
                    <div
                      key={src + i}
                      className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-border/60 bg-surface"
                    >
                      <img src={src} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
                      {i === 0 && (
                        <span className="absolute start-2 top-2 rounded-md bg-[#5958F3] px-2 py-0.5 text-[10px] font-semibold text-white">
                          {t(`${ns}.photos.cover`)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))}
                        className="absolute end-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100"
                        aria-label={t(`${ns}.aria.removePhoto`)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 15 && (
                    <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-surface text-xs font-semibold text-muted-foreground transition hover:border-[#5958F3] hover:text-[#5958F3]">
                      <Plus className="mb-1 h-5 w-5" />
                      {t(`${ns}.photos.addMore`)}
                      <input type="file" accept="image/*" multiple onChange={onPickPhotos} className="hidden" />
                    </label>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <ImageIcon className="h-3.5 w-3.5" /> {t(`${ns}.photos.uploaded`, { count: photos.length })}
                  </span>
                  <span>{t(`${ns}.photos.max`)}</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-[#5958F3] transition-all"
                    style={{ width: `${(photos.length / 15) * 100}%` }}
                  />
                </div>
              </Section>

              {/* Mood Tags */}
              <Section title={t(`${ns}.sections.moodTags`)} icon={<Sparkles className="h-4 w-4" />}>
                <p className="mb-3 text-xs text-muted-foreground">
                  {t(`${ns}.moodSection.desc`)}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {MOODS.map((m) => {
                    const on = moods.includes(m.key);
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => toggle(moods, m.key, setMoods)}
                        className={cn(
                          "flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition",
                          on
                            ? "border-[#5958F3] bg-[#5958F3]/10 text-foreground"
                            : "border-border bg-background hover:border-[#5958F3]/50",
                        )}
                      >
                        <span className="text-base leading-none">{m.emoji}</span>
                        <span>{t(`${ns}.moods.${m.key}`)}</span>
                      </button>
                    );
                  })}
                </div>
              </Section>

              {/* Amenities */}
              <Section
                title={t(`${ns}.sections.amenities`)}
                icon={<Check className="h-4 w-4" />}
                right={<span className="text-xs text-muted-foreground">{t(`${ns}.amenitiesSection.selected`, { count: amenities.length })}</span>}
              >
                <div className="space-y-5">
                  {amenityGroups.map((g) => (
                    <div key={g.group}>
                      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {groupLabel(g.group)}
                      </p>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {g.items.map((a) => {
                          const on = amenities.includes(a.key);
                          const Icon = a.icon;
                          return (
                            <button
                              key={a.key}
                              type="button"
                              onClick={() => toggle(amenities, a.key, setAmenities)}
                              className={cn(
                                "flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition",
                                on
                                  ? "border-[#5958F3] bg-[#5958F3]/10"
                                  : "border-border bg-background hover:border-[#5958F3]/50",
                              )}
                            >
                              <span
                                className={cn(
                                  "grid h-5 w-5 place-items-center rounded-md border",
                                  on ? "border-[#5958F3] bg-[#5958F3] text-white" : "border-border",
                                )}
                              >
                                {on && <Check className="h-3 w-3" />}
                              </span>
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <span>{amenityLabel(a)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Add new amenity */}
                  <div className="rounded-xl border border-dashed border-border p-3.5">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t(`${ns}.amenitiesSection.addNew`)}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                      <Input
                        placeholder={t(`${ns}.placeholders.amenityName`)}
                        value={newAmenityLabel}
                        onChange={(e) => setNewAmenityLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addAmenity();
                          }
                        }}
                      />
                      <select
                        value={newAmenityGroup}
                        onChange={(e) => setNewAmenityGroup(e.target.value)}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                      >
                        {amenityGroups.map((g) => (
                          <option key={g.group} value={g.group}>{groupLabel(g.group)}</option>
                        ))}
                        <option value="__new__">{t(`${ns}.amenitiesSection.newGroupOption`)}</option>
                      </select>
                      <Button
                        type="button"
                        onClick={addAmenity}
                        disabled={!newAmenityLabel.trim() || (newAmenityGroup === "__new__" && !newAmenityCustomGroup.trim())}
                        className="bg-[#5958F3] text-white hover:bg-[#5958F3]/90"
                      >
                        <Plus className="me-1 h-4 w-4" /> {t(`${ns}.amenitiesSection.addButton`)}
                      </Button>
                      {newAmenityGroup === "__new__" && (
                        <Input
                          className="sm:col-span-3"
                          placeholder={t(`${ns}.placeholders.newGroupName`)}
                          value={newAmenityCustomGroup}
                          onChange={(e) => setNewAmenityCustomGroup(e.target.value)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Section>

              {/* House Rules */}
              <Section title={t(`${ns}.sections.houseRules`)} icon={<Lightbulb className="h-4 w-4" />}>
                <p className="mb-3 text-xs text-muted-foreground">
                  {t(`${ns}.houseRulesSection.desc`)}
                </p>
                <div className="divide-y divide-border/60 rounded-xl border border-border/60">
                  {HOUSE_RULES.map((key) => (
                    <div key={key} className="flex items-center justify-between gap-4 p-3.5">
                      <div>
                        <p className="text-sm font-semibold">{t(`${ns}.houseRules.${key}.label`)}</p>
                        <p className="text-xs text-muted-foreground">{t(`${ns}.houseRules.${key}.desc`)}</p>
                      </div>
                      <Switch
                        checked={!!rules[key]}
                        onCheckedChange={(v) => setRules((p) => ({ ...p, [key]: v }))}
                        className="data-[state=checked]:bg-[#5958F3]"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(`${ns}.houseRulesSection.additionalLabel`)}
                  </Label>
                  <Textarea
                    value={extraRules}
                    onChange={(e) => setExtraRules(e.target.value)}
                    placeholder={t(`${ns}.placeholders.extraRules`)}
                    className="min-h-24 rounded-xl"
                  />
                </div>
              </Section>

              {/* Search Tags */}
              <Section title={t(`${ns}.sections.searchTags`)} icon={<Tag className="h-4 w-4" />}>
                <p className="mb-3 text-xs text-muted-foreground">
                  {t(`${ns}.tagsSection.desc`)}
                </p>
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-background p-2.5">
                  {tags.map((tg) => (
                    <span
                      key={tg}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#5958F3]/10 px-2.5 py-1 text-xs font-medium text-[#5958F3]"
                    >
                      {tg}
                      <button
                        type="button"
                        onClick={() => setTags((p) => p.filter((x) => x !== tg))}
                        aria-label={t(`${ns}.tagsSection.removeAria`, { tag: tg })}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder={t(`${ns}.placeholders.tagInput`)}
                    className="flex-1 min-w-32 bg-transparent px-1 py-1 text-xs outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  {t(`${ns}.tagsSection.hint`)}
                </p>
              </Section>
            </>
          )}

          {step === 3 && (
            <>
              <Section
                title={t(`${ns}.sections.weeklySchedule`)}
                icon={<CalendarIcon className="h-4 w-4" />}
                right={
                  <button
                    type="button"
                    onClick={() => {
                      const src = schedule.Monday;
                      setSchedule((p) => {
                        const next = { ...p };
                        DAYS.forEach((d) => {
                          if (next[d].open) next[d] = { ...src };
                        });
                        return next;
                      });
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-500/15 dark:text-emerald-400"
                  >
                    <Copy className="h-3 w-3" /> {t(`${ns}.schedule.copyAll`)}
                  </button>
                }
              >
                <p className="mb-4 text-xs text-muted-foreground">
                  {t(`${ns}.schedule.desc`)}
                </p>
                <div className="space-y-2.5">
                  {DAYS.map((day) => {
                    const d = schedule[day];
                    return (
                      <div
                        key={day}
                        className={cn(
                          "flex flex-wrap items-center gap-3 rounded-xl border p-3 transition sm:flex-nowrap",
                          d.open ? "border-[#5958F3]/25 bg-[#5958F3]/5" : "border-border/60 bg-muted/30",
                        )}
                      >
                        <Switch
                          checked={d.open}
                          onCheckedChange={(v) =>
                            setSchedule((p) => ({ ...p, [day]: { ...p[day], open: v } }))
                          }
                          className="data-[state=checked]:bg-[#5958F3]"
                        />
                        <span className="min-w-20 text-sm font-semibold">{t(`${ns}.days.${day}`)}</span>
                        {d.open ? (
                          <>
                            <TimeInput
                              value={d.from}
                              onChange={(v) => setSchedule((p) => ({ ...p, [day]: { ...p[day], from: v } }))}
                            />
                            <span className="text-muted-foreground">–</span>
                            <TimeInput
                              value={d.to}
                              onChange={(v) => setSchedule((p) => ({ ...p, [day]: { ...p[day], to: v } }))}
                            />
                            <button
                              type="button"
                              className="ms-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-[#5958F3] hover:bg-[#5958F3]/10"
                            >
                              <Plus className="h-3.5 w-3.5" /> {t(`${ns}.schedule.addSlot`)}
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {t(`${ns}.schedule.closed`)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Section>

              <Section title={t(`${ns}.sections.bookingRules`)} icon={<SlidersHorizontal className="h-4 w-4" />}>
                <p className="mb-4 text-xs text-muted-foreground">
                  {t(`${ns}.bookingRulesSection.desc`)}
                </p>
                <div className="divide-y divide-border/60 rounded-xl border border-border/60">
                  <RuleRow
                    label={t(`${ns}.bookingRulesSection.minDuration.label`)}
                    desc={t(`${ns}.bookingRulesSection.minDuration.desc`)}
                    icon={<Clock className="h-3.5 w-3.5" />}
                    value={minDur}
                    options={DUR_OPTS}
                    optionLabel={(o) => t(`${ns}.durationOptions.${o}`)}
                    onChange={setMinDur}
                    isAr={isAr}
                  />
                  <RuleRow
                    label={t(`${ns}.bookingRulesSection.maxDuration.label`)}
                    desc={t(`${ns}.bookingRulesSection.maxDuration.desc`)}
                    icon={<Clock className="h-3.5 w-3.5" />}
                    value={maxDur}
                    options={DUR_OPTS}
                    optionLabel={(o) => t(`${ns}.durationOptions.${o}`)}
                    onChange={setMaxDur}
                    isAr={isAr}
                  />
                  <RuleRow
                    label={t(`${ns}.bookingRulesSection.buffer.label`)}
                    desc={t(`${ns}.bookingRulesSection.buffer.desc`)}
                    icon={<Clock className="h-3.5 w-3.5" />}
                    value={buffer}
                    options={BUFFER_OPTS}
                    optionLabel={(o) => t(`${ns}.bufferOptions.${o}`)}
                    onChange={setBuffer}
                    isAr={isAr}
                  />
                  <div className="flex items-center justify-between gap-4 p-3.5">
                    <div>
                      <p className="text-sm font-semibold">{t(`${ns}.bookingRulesSection.maxGuests.label`)}</p>
                      <p className="text-xs text-muted-foreground">{t(`${ns}.bookingRulesSection.maxGuests.desc`)}</p>
                    </div>
                    <Stepper value={maxGuests} onChange={setMaxGuests} min={1} max={200} t={t} />
                  </div>
                  <RuleRow
                    label={t(`${ns}.bookingRulesSection.advWindow.label`)}
                    desc={t(`${ns}.bookingRulesSection.advWindow.desc`)}
                    icon={<CalendarIcon className="h-3.5 w-3.5" />}
                    value={advWindow}
                    options={WINDOW_OPTS}
                    optionLabel={(o) => t(`${ns}.windowOptions.${o}`)}
                    onChange={setAdvWindow}
                    isAr={isAr}
                  />
                  <RuleRow
                    label={t(`${ns}.bookingRulesSection.cutoff.label`)}
                    desc={t(`${ns}.bookingRulesSection.cutoff.desc`)}
                    icon={<Clock className="h-3.5 w-3.5" />}
                    value={cutoff}
                    options={CUTOFF_OPTS}
                    optionLabel={(o) => t(`${ns}.cutoffOptions.${o}`)}
                    onChange={setCutoff}
                    isAr={isAr}
                  />
                </div>
              </Section>

              <Section
                title={t(`${ns}.sections.dateOverrides`)}
                icon={<CalendarIcon className="h-4 w-4" />}
                right={<span className="text-[11px] text-muted-foreground">{t(`${ns}.overridesSection.badge`)}</span>}
              >
                <p className="mb-4 text-xs text-muted-foreground">
                  {t(`${ns}.overridesSection.desc`)}
                </p>
                <div className="space-y-2.5">
                  {overrides.map((o, i) => (
                    <div
                      key={i}
                      className="flex flex-wrap items-center gap-3 rounded-xl border border-border/60 bg-background p-3 sm:flex-nowrap"
                    >
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold">
                        <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        {o.date}
                      </span>
                      <span
                        className={cn(
                          "rounded-md px-2 py-0.5 text-[10px] font-semibold",
                          o.kind === "Closed"
                            ? "bg-red-500/10 text-red-600"
                            : "bg-[#5958F3]/10 text-[#5958F3]",
                        )}
                      >
                        {t(`${ns}.overridesSection.kinds.${o.kind}`)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                        {o.note}
                      </span>
                      <button
                        type="button"
                        onClick={() => setOverrides((p) => p.filter((_, idx) => idx !== i))}
                        className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-red-500/10 hover:text-red-600"
                        aria-label={t(`${ns}.overridesSection.removeAria`)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setOverrides((p) => [
                        ...p,
                        { date: t(`${ns}.overridesSection.newDate`), kind: "Closed", note: t(`${ns}.overridesSection.newNote`) },
                      ])
                    }
                    className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-border py-3 text-xs font-semibold text-muted-foreground transition hover:border-[#5958F3] hover:text-[#5958F3]"
                  >
                    <Plus className="h-4 w-4" /> {t(`${ns}.overridesSection.addButton`)}
                  </button>
                </div>
              </Section>
            </>
          )}


          {step === 4 && (
            <>
              <Section title={t(`${ns}.sections.basePricing`)} icon={<DollarSign className="h-3.5 w-3.5" />}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t(`${ns}.fields.currency`)}>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="h-11 w-full appearance-none rounded-xl border border-border bg-background px-3 text-sm font-medium outline-none transition focus:border-[#5958F3]"
                    >
                      {["SAR", "AED", "USD", "EUR"].map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t(`${ns}.fields.pricePerHour`)}>
                    <PriceInput currency={currency} value={hourly} onChange={setHourly} suffix="/ hr" />
                  </Field>
                  <Field label={t(`${ns}.fields.pricePerDay`)}>
                    <PriceInput currency={currency} value={daily} onChange={setDaily} suffix="/ day" />
                  </Field>
                  <Field label={t(`${ns}.fields.weeklyPackage`)}>
                    <PriceInput currency={currency} value={weeklyPkg} onChange={setWeeklyPkg} suffix="/ wk" />
                  </Field>
                  <Field label={t(`${ns}.fields.monthlyPackage`)}>
                    <PriceInput currency={currency} value={monthlyPkg} onChange={setMonthlyPkg} suffix="/ mo" />
                  </Field>
                  <Field label={t(`${ns}.fields.estHours`)}>
                    <div className="flex items-center gap-3">
                      <Stepper value={estHours} onChange={setEstHours} min={0} max={720} t={t} />
                      <span className="text-xs text-muted-foreground">{t(`${ns}.fields.hrsSuffix`)}</span>
                    </div>
                  </Field>
                </div>
              </Section>

              <Section title={t(`${ns}.sections.discounts`)} icon={<Percent className="h-3.5 w-3.5" />}>
                <div className="divide-y divide-border/60 rounded-xl border border-border/60">
                  <DiscountRow
                    label={t(`${ns}.pricingSection.discountRows.weekend.label`)}
                    desc={t(`${ns}.pricingSection.discountRows.weekend.desc`)}
                    enabled={weekendPricing}
                    onToggle={setWeekendPricing}
                    value={weekendUplift}
                    onValueChange={setWeekendUplift}
                    unit="+%"
                  />
                  <DiscountRow
                    label={t(`${ns}.pricingSection.discountRows.earlyBird.label`)}
                    desc={t(`${ns}.pricingSection.discountRows.earlyBird.desc`)}
                    enabled={earlyBird}
                    onToggle={setEarlyBird}
                    value={earlyBirdPct}
                    onValueChange={setEarlyBirdPct}
                    unit="-%"
                  />
                  <DiscountRow
                    label={t(`${ns}.pricingSection.discountRows.weeklyDisc.label`)}
                    desc={t(`${ns}.pricingSection.discountRows.weeklyDisc.desc`)}
                    enabled={weeklyDisc}
                    onToggle={setWeeklyDisc}
                    value={weeklyDiscPct}
                    onValueChange={setWeeklyDiscPct}
                    unit="-%"
                  />
                </div>
              </Section>

              <Section title={t(`${ns}.sections.depositCancellation`)} icon={<Wallet className="h-3.5 w-3.5" />}>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 p-4">
                  <div>
                    <p className="text-sm font-semibold">{t(`${ns}.pricingSection.deposit.label`)}</p>
                    <p className="text-xs text-muted-foreground">{t(`${ns}.pricingSection.deposit.desc`)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {depositRequired && (
                      <div className="relative">
                        <input
                          type="number"
                          value={depositPct}
                          onChange={(e) => setDepositPct(Number(e.target.value))}
                          className="h-9 w-20 rounded-lg border border-border bg-background ps-2 pe-6 text-sm font-semibold outline-none transition focus:border-[#5958F3]"
                        />
                        <span className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                      </div>
                    )}
                    <Switch checked={depositRequired} onCheckedChange={setDepositRequired} />
                  </div>
                </div>

                <div className="mt-4">
                  <Label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(`${ns}.pricingSection.cancellationLabel`)}
                  </Label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {CANCEL_POLICIES.map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setCancelPolicy(key)}
                        className={cn(
                          "rounded-xl border p-3 text-start transition",
                          cancelPolicy === key
                            ? "border-[#5958F3] bg-[#5958F3]/5 ring-1 ring-[#5958F3]"
                            : "border-border/60 hover:border-[#5958F3]/40",
                        )}
                      >
                        <p className="text-sm font-semibold">{t(`${ns}.pricingSection.cancellationPolicies.${key}.label`)}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">{t(`${ns}.pricingSection.cancellationPolicies.${key}.desc`)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </Section>

              <Section title={t(`${ns}.sections.commissionFees`)} icon={<Info className="h-3.5 w-3.5" />}>
                <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-xs text-muted-foreground">
                  <p>
                    {t(`${ns}.pricingSection.commissionNote`, { platformFeePct, gatewayPct })}
                  </p>
                </div>
              </Section>

              <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-500 text-white">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="font-medium text-emerald-700 dark:text-emerald-400">
                  {t(`${ns}.pricingSection.allComplete`)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer nav */}
      <Card className="mt-6 flex flex-col items-center justify-between gap-3 rounded-2xl border-border/60 p-4 shadow-soft sm:flex-row sm:p-5">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="h-10 rounded-xl"
            disabled={step === 1}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
          >
            <BackChevron className="h-4 w-4" /> {t(`${ns}.back`)}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t(`${ns}.footerStep`, { step, label: STEPS[step - 1].label })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" className="h-10 rounded-xl">
            <Link to="/merchant/spaces">{t(`${ns}.cancel`)}</Link>
          </Button>
          {step < 4 ? (
            <Button
              onClick={() => setStep((s) => Math.min(4, s + 1))}
              className="h-10 rounded-xl bg-[#5958F3] text-white shadow-soft hover:bg-[#4948d8]"
            >
              {t(`${ns}.nextWithLabel`, { label: STEPS[step].label })} <ForwardChevron className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={() => navigate({ to: "/merchant/spaces" })}
              className="h-10 rounded-xl bg-[#5958F3] text-white shadow-soft hover:bg-[#4948d8]"
            >
              {isEdit ? t(`${ns}.saveChanges`) : t(`${ns}.publishSpace`)} <Check className="h-4 w-4" />
            </Button>
          )}
        </div>
      </Card>
    </MerchantShell>
  );
}

function Section({
  title,
  icon,
  right,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="rounded-2xl border-border/60 p-5 shadow-soft sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-base font-bold">
          {icon && (
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#5958F3]/10 text-[#5958F3]">
              {icon}
            </span>
          )}
          {title}
        </h2>
        {right}
      </div>
      {children}
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Stepper({
  value, onChange, min = 0, max = 999, t,
}: { value: number; onChange: (n: number) => void; min?: number; max?: number; t: (key: string) => string }) {
  return (
    <div className="inline-flex h-11 items-center rounded-xl border border-border bg-background">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="grid h-full w-11 place-items-center rounded-s-xl text-muted-foreground hover:bg-muted"
        aria-label={t("pages.merchant.spacesNew.aria.decrease")}
      >
        <Minus className="h-4 w-4" />
      </button>
      <div className="grid h-full w-16 place-items-center border-x border-border text-sm font-bold">
        {value}
      </div>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        className="grid h-full w-11 place-items-center rounded-e-xl text-muted-foreground hover:bg-muted"
        aria-label={t("pages.merchant.spacesNew.aria.increase")}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

function TimeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <Clock className="pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-32 rounded-lg border border-border bg-background ps-8 pe-2 text-sm outline-none transition focus:border-[#5958F3]"
      />
    </div>
  );
}

function RuleRow({
  label, desc, icon, value, options, optionLabel, onChange, isAr,
}: {
  label: string;
  desc: string;
  icon: React.ReactNode;
  value: string;
  options: string[];
  optionLabel: (v: string) => string;
  onChange: (v: string) => void;
  isAr: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 p-3.5">
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <div className="relative">
        <span className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 appearance-none rounded-lg border border-border bg-background ps-8 pe-8 text-sm font-medium outline-none transition focus:border-[#5958F3]"
        >
          {options.map((o) => (
            <option key={o} value={o}>{optionLabel(o)}</option>
          ))}
        </select>
        {isAr ? (
          <ChevronLeft className="pointer-events-none absolute end-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90 text-muted-foreground" />
        ) : (
          <ChevronRight className="pointer-events-none absolute end-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90 text-muted-foreground" />
        )}
      </div>
    </div>
  );
}

function MiniCalendar({
  schedule, dayLabel, isAr,
}: { schedule: Record<string, { open: boolean; from: string; to: string }>; dayLabel: (d: string) => string; isAr: boolean }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const today = new Date();
  const view = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = view.toLocaleString(isAr ? "ar" : "en-US", { month: "long", year: "numeric" });
  const firstDay = view.getDay(); // 0=Sun
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const weekDayMap = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const dayNames = weekDayMap.map((d) => dayLabel(d).slice(0, 2));
  const PrevChevron = isAr ? ChevronRight : ChevronLeft;
  const NextChevron = isAr ? ChevronLeft : ChevronRight;

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setMonthOffset((m) => m - 1)}
          className="grid h-7 w-7 place-items-center rounded-lg text-muted-foreground hover:bg-muted"
          aria-label="Previous month"
        >
          <PrevChevron className="h-3.5 w-3.5" />
        </button>
        <p className="text-sm font-semibold">{monthLabel}</p>
        <button
          type="button"
          onClick={() => setMonthOffset((m) => m + 1)}
          className="grid h-7 w-7 place-items-center rounded-lg text-muted-foreground hover:bg-muted"
          aria-label="Next month"
        >
          <NextChevron className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {dayNames.map((d, i) => (
          <div key={i} className="py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const date = new Date(view.getFullYear(), view.getMonth(), d);
          const weekday = weekDayMap[date.getDay()];
          const open = schedule[weekday]?.open;
          const partial = open && (schedule[weekday].to <= "16:00");
          const isToday =
            date.toDateString() === today.toDateString();
          return (
            <div
              key={d}
              className={cn(
                "grid aspect-square place-items-center rounded-lg font-medium transition",
                !open && "text-muted-foreground/50",
                open && !partial && "bg-[#5958F3]/10 text-[#5958F3]",
                partial && "bg-amber-500/15 text-amber-700 dark:text-amber-400",
                isToday && "ring-2 ring-[#5958F3] ring-offset-1 ring-offset-background",
              )}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}



function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-semibold", muted ? "text-muted-foreground" : "text-foreground")}>{value}</span>
    </div>
  );
}

function PriceInput({
  currency, value, onChange, suffix,
}: { currency: string; value: number; onChange: (n: number) => void; suffix: string }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground">
        {currency}
      </span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-11 w-full rounded-xl border border-border bg-background ps-12 pe-14 text-sm font-semibold outline-none transition focus:border-[#5958F3]"
      />
      <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
        {suffix}
      </span>
    </div>
  );
}

function DiscountRow({
  label, desc, enabled, onToggle, value, onValueChange, unit,
}: {
  label: string;
  desc: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  value: number;
  onValueChange: (n: number) => void;
  unit: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 p-4">
      <div className="min-w-0">
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <div className="flex items-center gap-3">
        {enabled && (
          <div className="relative">
            <input
              type="number"
              value={value}
              onChange={(e) => onValueChange(Number(e.target.value))}
              className="h-9 w-24 rounded-lg border border-border bg-background ps-2 pe-10 text-sm font-semibold outline-none transition focus:border-[#5958F3]"
            />
            <span className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-muted-foreground">
              {unit}
            </span>
          </div>
        )}
        <Switch checked={enabled} onCheckedChange={onToggle} />
      </div>
    </div>
  );
}
